import {
  connect,
  type ConnectProject,
  type ConnectUser,
  type ExtensionMainMenu,
  type WorkspaceAPI,
  type WorkspaceEventCallback,
} from "trimble-connect-workspace-api";
import { Emitter } from "../util/emitter";
import { errorMessage } from "../util/format";

export type HostKind = "3dviewer" | "project" | "standalone";

type WorkspaceEvents = {
  token: string;
  project: ConnectProject | null;
  /** Menu/command clicks forwarded from the host application. */
  command: string;
  /** The user's chosen UI language (raw `UserSettings.language`), initial and on change. */
  language: string;
};

/**
 * Wraps the Trimble Connect Workspace API.
 *
 * Connect embeds the extension in an iframe and speaks `postMessage`; the handshake fails
 * (by timing out) when the page is opened directly, which is the normal case during
 * development. That is treated as a supported "standalone" mode rather than an error, so
 * the viewer still runs against local files.
 */
export class WorkspaceBridge {
  readonly events = new Emitter<WorkspaceEvents>();
  api: WorkspaceAPI | null = null;
  host: HostKind = "standalone";
  project: ConnectProject | null = null;
  /** Raw `UserSettings.language` from the host, e.g. `"de"` or `"fr-FR"`; `null` until known. */
  language: string | null = null;

  private token: string | null = null;
  private tokenWaiters: Array<(token: string) => void> = [];

  get connected(): boolean {
    return this.api !== null;
  }

  /** True when the page is actually inside another window, i.e. worth attempting a handshake. */
  static isEmbedded(): boolean {
    try {
      return window.parent !== window;
    } catch {
      return true;
    }
  }

  async connect(timeoutMs = 8000): Promise<void> {
    if (!WorkspaceBridge.isEmbedded()) return;
    try {
      // The published callback type is a union of one signature per event; the bridge
      // dispatches on the event id itself, so it is typed generically and cast here.
      const handler = ((event: string, data: unknown) => this.onEvent(event, data)) as WorkspaceEventCallback;
      this.api = await connect(window.parent, handler, timeoutMs);
    } catch (error) {
      console.warn("[workspace] handshake failed, continuing standalone:", errorMessage(error));
      this.api = null;
      return;
    }

    try {
      const hostInfo = await this.api.extension.getHost();
      this.host = hostInfo?.name === "project" ? "project" : "3dviewer";
    } catch {
      this.host = "3dviewer";
    }

    try {
      this.project = await this.api.project.getProject();
      this.events.emit("project", this.project);
    } catch (error) {
      console.warn("[workspace] could not read the project:", errorMessage(error));
    }

    try {
      const settings = await this.api.user.getSettings();
      if (settings?.language) {
        this.language = settings.language;
        this.events.emit("language", settings.language);
      }
    } catch (error) {
      console.warn("[workspace] could not read the user's language setting:", errorMessage(error));
    }
  }

  private onEvent(event: string, data: unknown): void {
    if (event === "extension.accessToken") {
      const token = (data as { data?: string } | undefined)?.data;
      if (typeof token === "string" && token.length > 0) this.setToken(token);
      return;
    }
    if (event === "extension.command") {
      const command = (data as { data?: string } | undefined)?.data;
      if (typeof command === "string") this.events.emit("command", command);
      return;
    }
    if (event === "project.onChanged") {
      const changed = (data as { data?: { new?: ConnectProject } } | undefined)?.data?.new;
      if (changed) {
        this.project = changed;
        this.events.emit("project", changed);
      }
      return;
    }
    if (event === "user.onSettingsChanged") {
      const language = (data as { data?: { new?: { language?: string } } } | undefined)?.data?.new?.language;
      if (typeof language === "string" && language) {
        this.language = language;
        this.events.emit("language", language);
      }
    }
  }

  private setToken(token: string): void {
    this.token = token;
    const waiters = this.tokenWaiters;
    this.tokenWaiters = [];
    for (const resolve of waiters) resolve(token);
    this.events.emit("token", token);
  }

  /** Manually supplied token, used when running outside Connect. */
  useToken(token: string): void {
    this.setToken(token);
  }

  /**
   * Obtains a Trimble ID access token for the Core API.
   *
   * `requestPermission` either returns the token directly or the string `"pending"` while
   * Connect asks the user to consent; in the pending case the token arrives later on the
   * `extension.accessToken` event.
   */
  async accessToken(timeoutMs = 60_000): Promise<string> {
    if (this.token) return this.token;
    if (!this.api) throw new Error("Not connected to Trimble Connect — paste an access token to browse project files");

    const status = await this.api.extension.requestPermission("accesstoken");
    if (typeof status === "string" && looksLikeToken(status)) {
      this.setToken(status);
      return status;
    }
    if (status === "denied") throw new Error("Access to Trimble Connect data was denied");

    return new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.tokenWaiters = this.tokenWaiters.filter((w) => w !== onToken);
        reject(new Error("Timed out waiting for the Trimble Connect access token"));
      }, timeoutMs);
      const onToken = (token: string) => {
        clearTimeout(timer);
        resolve(token);
      };
      this.tokenWaiters.push(onToken);
    });
  }

  hasToken(): boolean {
    return this.token !== null;
  }

  /**
   * The signed-in Trimble Connect user, used to gate the app against the server's
   * whitelist (see `App`'s auth gate) — the server re-verifies this independently against
   * Trimble's own identity provider rather than trusting it, but the email is shown in the
   * gate UI before that round trip completes.
   */
  async getUser(): Promise<ConnectUser | null> {
    if (!this.api) return null;
    try {
      return await this.api.user.getUser();
    } catch (error) {
      console.warn("[workspace] could not read the signed-in user:", errorMessage(error));
      return null;
    }
  }

  async setStatus(message: string): Promise<void> {
    try {
      await this.api?.extension.setStatusMessage(message);
    } catch {
      // Status messages are cosmetic; never let them break a load.
    }
  }

  /**
   * Configures Trimble Connect's web navigation menu for this extension. Only meaningful
   * for a "project"-hosted extension (see `host`); clicking an entry comes back as an
   * `extension.command` event, forwarded on `events` as "command".
   */
  async setMenu(menu: ExtensionMainMenu): Promise<void> {
    if (!this.api) return;
    try {
      await this.api.ui.setMenu(menu);
    } catch (error) {
      console.warn("[workspace] could not set the navigation menu:", errorMessage(error));
    }
  }

  /** Highlights one entry of the navigation menu set via `setMenu` — e.g. which of the
   *  submenu items (3D viewer / orthophoto plan) is the one currently showing. */
  async setActiveMenuItem(command: string): Promise<void> {
    if (!this.api) return;
    try {
      await this.api.ui.setActiveMenuItem(command);
    } catch (error) {
      console.warn("[workspace] could not set the active navigation menu item:", errorMessage(error));
    }
  }
}

/** A JWT has three dot-separated segments; `"pending"` and `"denied"` obviously do not. */
function looksLikeToken(value: string): boolean {
  return value.split(".").length === 3 && value.length > 40;
}
